import { useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  ENTERPRISE_PRICING,
  SEAT_MIN,
  SEAT_MAX,
  clampSeats,
  enterpriseTotal,
  annualSavings,
  annualDiscountPercent,
} from '../lib/enterprisePricing';

interface Props {
  onClose: () => void;
  hasActiveIndividualSub: boolean;
}

export function EnterpriseUpgradeModal({ onClose, hasActiveIndividualSub }: Props) {
  const [companyName, setCompanyName] = useState('');
  const [seatCount, setSeatCount] = useState<number>(SEAT_MIN);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  // Whether the upgrading user takes one of the seats themselves. Defaulted on
  // here more strongly than at signup — they are already using TrueLoad, so
  // upgrading without a seat would silently cost them Results access.
  const [takeSeat, setTakeSeat] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { monthlySeat, annualSeat } = ENTERPRISE_PRICING;
  const savings = annualSavings(seatCount);

  const canSubmit =
    companyName.trim().length > 0 && seatCount >= SEAT_MIN && seatCount <= SEAT_MAX;

  async function handleSubmit() {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not signed in');
      const res = await fetch('/.netlify/functions/create-enterprise-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: companyName.trim(),
          seat_count: seatCount,
          billing_cycle: billingCycle,
          take_seat: takeSeat,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Upgrade to Enterprise</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {hasActiveIndividualSub && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <strong>You have an active individual subscription.</strong> Upgrading to Enterprise does not automatically cancel it. After upgrading, cancel your individual subscription from the Subscription tab to avoid double billing.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              maxLength={200}
              placeholder="e.g., Acme Electrical LLC"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number of seats</label>
            <input
              type="number"
              min={SEAT_MIN}
              max={SEAT_MAX}
              value={seatCount}
              onChange={(e) => setSeatCount(clampSeats(parseInt(e.target.value, 10)))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {SEAT_MIN} to {SEAT_MAX} seats — one per person who runs calculations. Managing the
              firm is free. Contact support for larger teams.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={takeSeat}
              onChange={(e) => setTakeSeat(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Give one of these seats to me
              <span className="block text-xs text-gray-500 mt-0.5">
                Turn this off only if you're buying for other people. Without a seat you'll
                administer the firm but won't be able to run calculations yourself.
              </span>
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing cycle</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-3 border rounded-lg text-sm transition-colors ${
                  billingCycle === 'monthly'
                    ? 'border-blue-600 bg-blue-50 text-blue-900 font-medium'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-3 border rounded-lg text-sm transition-colors relative ${
                  billingCycle === 'annual'
                    ? 'border-blue-600 bg-blue-50 text-blue-900 font-medium'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                  Save {annualDiscountPercent(seatCount)}%
                </span>
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Base fee</span>
              <span className="text-gray-900 font-medium">
                ${(billingCycle === 'monthly'
                    ? ENTERPRISE_PRICING.monthlyBase
                    : ENTERPRISE_PRICING.annualBase).toFixed(2)} / {billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">
                {seatCount} {seatCount === 1 ? 'seat' : 'seats'} × $
                {billingCycle === 'monthly' ? monthlySeat : annualSeat}
              </span>
              <span className="text-gray-900 font-medium">
                ${(billingCycle === 'monthly' ? monthlySeat * seatCount : annualSeat * seatCount).toFixed(2)} / {billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            <div className="border-t border-gray-300 pt-2 flex justify-between">
              <span className="text-gray-900 font-semibold">Total</span>
              <span className="text-gray-900 font-bold text-lg">
                ${enterpriseTotal(seatCount, billingCycle).toFixed(2)} / {billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            {billingCycle === 'annual' && (
              <div className="text-xs text-green-700 font-medium pt-1">
                You save ${savings.toFixed(2)}/yr compared to monthly billing.
              </div>
            )}
          </div>

          {billingCycle === 'annual' && (
            <p className="text-xs text-gray-500">
              Annual plans are billed upfront and committed for one year. Monthly plans can be canceled anytime.
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Redirecting...</>
            ) : (
              'Continue to Checkout'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
